<template>
  <!-- 🔴 THE GEOMETRY IS LOAD-BEARING. A fixed `height` (not a content-sized dialog) is what gives the two
       columns a height to divide between their own scrollers; without it the editor column grows forever and
       the preview walks off the bottom of the screen. Same shape, same reason, as TaxQuestionItemV2Dialog. -->
  <v-dialog
    v-model="dialogModel"
    max-width="min(96vw, 1440px)"
    height="88vh"
    persistent
    scrollable
    data-testid="mfcc-dialog"
  >
    <v-card class="mfcc-card">
      <v-card-title class="mfcc-header">
        <div class="mfcc-heading">
          <div class="mfcc-header-icon">
            <v-icon size="24">mdi-card-account-details-outline</v-icon>
          </div>
          <div>
            <div class="mfcc-title">Tax card configs</div>
            <div class="mfcc-subtitle">
              Ready-made card payloads other features fetch by config code. Reorder with drag or the arrows, then press SAVE once.
            </div>
          </div>
        </div>
        <v-btn icon="mdi-close" variant="text" aria-label="Close tax card configs" @click="closeDialog" />
      </v-card-title>

      <v-progress-linear v-if="loading || saving" indeterminate color="primary" height="3" />
      <v-divider v-else />

      <v-card-text class="mfcc-split pa-0">
        <!-- ══ LEFT: the library ═══════════════════════════════════════════════════════════════════
             Fixed width and its own scroller: this column is a table of contents, and it has to stay
             put while the editor beside it scrolls. -->
        <div class="mfcc-list-col">
          <div class="mfcc-list-head">
            <div>
              <div class="mfcc-list-title">Cards</div>
              <div class="mfcc-list-count">{{ items.length }} {{ items.length === 1 ? 'config' : 'configs' }}</div>
            </div>
            <v-btn
              color="primary"
              variant="tonal"
              size="small"
              prepend-icon="mdi-plus"
              data-testid="mfcc-add"
              @click="addItem"
            >
              ADD ITEM
            </v-btn>
          </div>

          <div v-if="!items.length && !loading" class="mfcc-list-empty">
            No card configs yet. Press ADD ITEM to create the first one.
          </div>

          <!-- 🔴 `item-key="rowKey"` and NOT the index: reordering by index re-keys every row, so Vue
               rebuilds the DOM instead of moving one node — and the field the admin was typing in loses
               focus mid-drag. The setter renumbers `sortOrder` because a drag moves array elements and
               touches no number of its own. -->
          <draggable
            v-model="draggableItems"
            item-key="rowKey"
            class="mfcc-rows"
            handle=".drag-handle"
            ghost-class="mfcc-drag-ghost"
            chosen-class="mfcc-drag-chosen"
            :animation="180"
            :disabled="dragDisabled"
          >
            <template #item="{ element, index }">
              <div
                class="mfcc-row"
                :class="{ 'mfcc-row--selected': element.rowKey === selectedRowKey, 'mfcc-row--off': !element.isActive }"
                :data-testid="`mfcc-row-${index}`"
                @click="selectRow(element)"
              >
                <div class="mfcc-row-line">
                  <v-btn
                    class="drag-handle"
                    icon
                    variant="text"
                    density="compact"
                    :disabled="dragDisabled"
                    aria-label="Drag to reorder"
                    @click.stop
                  >
                    <v-icon size="18">mdi-drag-vertical</v-icon>
                  </v-btn>
                  <v-chip size="x-small" color="primary" variant="tonal" class="mfcc-row-order">{{ index + 1 }}</v-chip>
                  <div class="mfcc-row-name" :title="element.configName">
                    {{ element.configName || '(untitled)' }}
                  </div>
                </div>

                <div class="mfcc-row-line mfcc-row-line--meta">
                  <div class="mfcc-row-meta">
                    <div class="mfcc-row-code">{{ element.configCode || '(no code)' }}</div>
                    <v-chip
                      v-if="cardTypeOf(element.configJson)"
                      size="x-small"
                      variant="tonal"
                      color="indigo"
                      class="mfcc-row-type"
                    >
                      {{ cardTypeOf(element.configJson) }}
                    </v-chip>
                  </div>

                  <div class="mfcc-row-actions" @click.stop>
                    <v-switch
                      :model-value="element.isActive"
                      color="primary"
                      density="compact"
                      hide-details
                      inset
                      class="mfcc-row-switch"
                      :aria-label="`Active: ${element.configCode || 'row ' + (index + 1)}`"
                      :data-testid="`mfcc-active-${index}`"
                      @update:model-value="setActive(element, $event)"
                    />
                    <v-btn
                      icon
                      variant="text"
                      density="compact"
                      :disabled="index === 0"
                      aria-label="Move up"
                      :data-testid="`mfcc-up-${index}`"
                      @click="moveItem(index, -1)"
                    >
                      <v-icon size="18">mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      density="compact"
                      :disabled="index === items.length - 1"
                      aria-label="Move down"
                      :data-testid="`mfcc-down-${index}`"
                      @click="moveItem(index, 1)"
                    >
                      <v-icon size="18">mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      density="compact"
                      color="error"
                      aria-label="Delete card config"
                      :data-testid="`mfcc-delete-${index}`"
                      @click="removeItem(index)"
                    >
                      <v-icon size="18">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

          <div v-if="items.length" class="mfcc-list-foot">
            <v-btn
              variant="text"
              size="small"
              color="primary"
              prepend-icon="mdi-plus"
              data-testid="mfcc-add-bottom"
              @click="addItem"
            >
              ADD ITEM
            </v-btn>
          </div>
        </div>

        <!-- ══ RIGHT: the selected card ════════════════════════════════════════════════════════════
             Form and preview sit side by side on a wide screen and stack on a narrow one — see the
             media query at the bottom of the file. -->
        <div class="mfcc-editor-col">
          <v-alert
            v-if="validationErrors.length"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
            data-testid="mfcc-error"
          >
            <div class="font-weight-medium mb-1">This list cannot be saved yet</div>
            <ul class="mfcc-error-list">
              <li v-for="(message, i) in validationErrors" :key="i">{{ message }}</li>
            </ul>
          </v-alert>

          <v-alert
            v-if="loadError || saveError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
            data-testid="mfcc-server-error"
          >
            {{ loadError || saveError }}
          </v-alert>

          <v-alert
            v-if="saveSuccess"
            type="success"
            variant="tonal"
            density="compact"
            class="mb-4"
            data-testid="mfcc-success"
          >
            {{ saveSuccess }}
          </v-alert>

          <!-- Notes the server attached to a save that WENT THROUGH (a template path it could not
               resolve, say). Gated on `saveSuccess` on purpose: these describe one particular write,
               so they leave the screen the moment that write stops being the last thing that happened
               — every edit already clears `saveSuccess`. -->
          <v-alert
            v-if="saveSuccess && saveWarnings.length"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
            data-testid="mfcc-save-warnings"
          >
            <div class="font-weight-medium mb-1">Saved, with notes</div>
            <ul class="mfcc-error-list">
              <li v-for="(message, i) in saveWarnings" :key="i">{{ message }}</li>
            </ul>
          </v-alert>

          <div v-if="!selectedItem" class="mfcc-placeholder" data-testid="mfcc-no-selection">
            <v-icon size="40" color="grey">mdi-gesture-tap</v-icon>
            <div class="mfcc-placeholder-title">Nothing selected</div>
            <div class="mfcc-placeholder-text">
              Pick a card on the left to edit it, or press ADD ITEM to start a new one.
            </div>
          </div>

          <div v-else class="mfcc-editor-split">
            <div class="mfcc-form-col">
              <v-text-field
                :model-value="selectedItem.configName"
                label="Name *"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-label-outline"
                :maxlength="maxNameChars"
                counter
                persistent-counter
                hint="What an admin reads in the list. Any language."
                persistent-hint
                data-testid="mfcc-name"
                @update:model-value="setName"
              />

              <!-- 🔴 Upper-cased AS TYPED, not on blur: the code is the identity another feature fetches
                   by, the server upper-cases it before storing, and a box that shows one casing while the
                   database holds another is how two rows end up "different" and collide on save. -->
              <v-text-field
                :model-value="selectedItem.configCode"
                label="Config code *"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-identifier"
                :rules="codeRules"
                :maxlength="40"
                hint="A–Z, 0–9 and underscore, up to 40 characters — this is what other features fetch the card by."
                persistent-hint
                class="mt-3"
                data-testid="mfcc-code"
                @update:model-value="setCode"
              />

              <div class="mfcc-json-head">
                <span class="mfcc-json-label">Card JSON *</span>
                <v-menu>
                  <template #activator="{ props: menuProps }">
                    <v-btn
                      v-bind="menuProps"
                      variant="tonal"
                      size="small"
                      color="primary"
                      prepend-icon="mdi-file-document-outline"
                      data-testid="mfcc-sample-menu"
                    >
                      Insert sample
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item
                      v-for="sample in samples"
                      :key="sample.key"
                      :title="sample.label"
                      :subtitle="sample.cardType"
                      :data-testid="`mfcc-sample-${sample.key}`"
                      @click="requestSample(sample)"
                    />
                  </v-list>
                </v-menu>
              </div>

              <v-textarea
                :model-value="selectedItem.configJson"
                class="code-area"
                variant="outlined"
                density="comfortable"
                auto-grow
                :rows="14"
                :max-rows="30"
                placeholder='{ "type": "TAX_FUND_FACTSHEET", "version": 1, "refId": "type8", "data": { … } }'
                :error="!!selectedJsonError"
                :error-messages="selectedJsonError ? [selectedJsonError] : []"
                data-testid="mfcc-json"
                @update:model-value="setJson"
              >
                <template #append-inner>
                  <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press
                       also focuses the textarea and drops the caret wherever the pointer landed — in a
                       document that is about to be reflowed, never where the author was looking. -->
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    :disabled="!canFormatJsonText(selectedItem.configJson)"
                    aria-label="Format JSON"
                    data-testid="mfcc-json-beautify"
                    @mousedown.prevent
                    @click.stop="beautifyJson"
                  >
                    <v-icon size="18">mdi-code-braces</v-icon>
                    <v-tooltip activator="parent" location="top">Format JSON</v-tooltip>
                  </v-btn>
                </template>
              </v-textarea>
            </div>

            <!-- READ-ONLY, and it has TWO SOURCES.
                 With no fund picked the card is the parse of what is in the box: a computed, in memory,
                 nothing to debounce and no request to make. Pick a fund and the SERVER renders the same
                 document against that fund's real rows in the chosen language, and that render is what
                 shows — the template's `{{ mf.fund.… }}` paths only mean something once there is a fund
                 to read them from. -->
            <div class="mfcc-preview-col" data-testid="mfcc-preview">
              <div class="mfcc-preview-label">Preview</div>

              <div class="mfcc-preview-controls">
                <!-- 🔴 `no-filter`. The server already decided what matches `q`; Vuetify's own filter
                     would run a SECOND pass over the rows it sent and hide the ones whose display title
                     does not contain the keyword — every fund matched on its CODE rather than its name. -->
                <v-autocomplete
                  :model-value="previewFundCodes"
                  :items="fundPickerItems"
                  :loading="fundSearchLoading"
                  :search="fundSearch"
                  :menu-props="{ maxHeight: 320 }"
                  :hint="fundPickerHint"
                  item-title="title"
                  item-value="fundCode"
                  label="Funds"
                  placeholder="Search by code or name"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  persistent-hint
                  no-filter
                  multiple
                  chips
                  closable-chips
                  clearable
                  data-testid="mfcc-fund-picker"
                  @update:focused="onPickerFocus"
                  @update:search="onFundSearch"
                  @update:model-value="setPreviewFunds"
                >
                  <template #item="{ props: itemProps, item }">
                    <!-- `null`, not `''`: Vuetify draws a subtitle row for any non-null value, and an
                         empty one is a blank line under every fund whose name IS its code. -->
                    <v-list-item
                      v-bind="itemProps"
                      :subtitle="item.raw.name ? item.raw.fundCode : null"
                    />
                  </template>
                </v-autocomplete>

                <div class="mfcc-preview-lang-row">
                  <v-btn-toggle
                    :model-value="previewLang"
                    color="primary"
                    density="compact"
                    variant="outlined"
                    divided
                    mandatory
                    class="mfcc-lang-toggle"
                    data-testid="mfcc-lang"
                    @update:model-value="setPreviewLang"
                  >
                    <v-btn value="th" size="small">TH</v-btn>
                    <v-btn value="en" size="small">EN</v-btn>
                    <v-btn value="zh" size="small">中</v-btn>
                  </v-btn-toggle>

                  <!-- Only when the server answered in a DIFFERENT language than the one asked for:
                       that is the card telling you a label somewhere has no text in that language, so
                       the whole document fell back to Thai. -->
                  <v-chip
                    v-if="previewLangNote"
                    size="x-small"
                    color="warning"
                    variant="tonal"
                    class="mfcc-lang-note"
                    data-testid="mfcc-preview-lang-note"
                  >
                    {{ previewLangNote }}
                  </v-chip>
                </div>
              </div>

              <!-- ABOVE the card, never in place of it: a bar that replaced the column would blank the
                   preview on every keystroke. Same reason the warning below sits above it too. -->
              <v-progress-linear
                :active="previewLoading"
                indeterminate
                height="3"
                rounded
                color="primary"
                class="mfcc-preview-bar"
                data-testid="mfcc-preview-busy"
              />

              <v-alert
                v-if="previewErrors.length"
                type="warning"
                variant="tonal"
                density="compact"
                class="mfcc-preview-alert"
                data-testid="mfcc-preview-error"
              >
                <div v-for="(message, i) in previewErrors" :key="i" class="text-caption">{{ message }}</div>
              </v-alert>

              <MutualFundCardPreview :card="previewSource.card" :raw="previewSource.raw" />
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <span v-if="items.length" class="text-caption text-medium-emphasis">
          SAVE replaces the whole library in one write.
        </span>
        <v-spacer />
        <v-btn variant="outlined" data-testid="mfcc-cancel" @click="closeDialog">
          <v-icon start>mdi-close</v-icon>CANCEL
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :disabled="!canSave"
          :loading="saving"
          data-testid="mfcc-save"
          @click="saveConfigs"
        >
          <v-icon start>mdi-check</v-icon>SAVE
        </v-btn>
      </v-card-actions>
    </v-card>

  </v-dialog>

  <!-- 🔴 A SIBLING of the editor dialog, not a child of it: `scrollable` makes the editor's own v-card the
       one scroller, and a second root inside that slot is a second thing for it to target. Overwriting a
       document the admin wrote is the one destructive thing the sample menu can do, so it asks first — in
       the app's own chrome, because a native modal inside a Vuetify dialog steals focus and comes back with
       the dialog scrolled somewhere else. -->
  <v-dialog v-model="sampleConfirmOpen" max-width="440" data-testid="mfcc-sample-confirm">
    <v-card>
      <v-card-title class="text-subtitle-1">Replace the card JSON?</v-card-title>
      <v-card-text class="text-body-2">
        This row already has a document. Inserting
        <strong>{{ pendingSample ? pendingSample.label : '' }}</strong>
        will overwrite it.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" data-testid="mfcc-sample-cancel" @click="pendingSample = null">Keep mine</v-btn>
        <v-btn color="primary" variant="flat" data-testid="mfcc-sample-confirm-ok" @click="confirmSample">
          Overwrite
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * MutualFundCardConfigDialog — the whole editor for `dbo.MutualFundCardConfig`, the system-wide library of
 * ready-made tax cards other features will fetch by `configCode`.
 *
 * ── The five things about this dialog that are not obvious ───────────────────────────────────────
 *
 * 1. IT LOADS AND SAVES ITSELF. `MutualFund.vue` only owns a boolean. The dialog fetches on open and
 *    writes on SAVE, exactly like `TaxMobileProductSettingsDialog`, because the host view is 6,000 lines
 *    and every piece of state that lives there is a piece two features can disagree about.
 *
 * 2. SAVE IS REPLACE-ALL. One POST carries the entire list; the server deletes what is not in it. That is
 *    why validation has to pass for the WHOLE list before the button lights up — a save that is rejected
 *    on row 12 leaves the library exactly as it was, but a save that got through with row 12 wrong would
 *    have rewritten all twelve.
 *
 * 3. POSITION IS THE ORDER. `sortOrder` is never typed and never trusted from what was loaded: drag and
 *    ↑/↓ move array elements, `applySortOrder` renumbers, and the payload sends the index anyway. The
 *    server renumbers a third time for the same reason.
 *
 * 4. NO Swal HERE. `api/call.js` already raises one on every transport failure. What this dialog adds is
 *    the inline message, because a `BadRequest` from the BAL carries the ACTUAL reason ("Row 3 (X) …") and
 *    the generic alert hides it.
 *
 * 5. THE PREVIEW HAS TWO MODES, and only one of them talks to the server. A card document is a TEMPLATE:
 *    `{{ mf.fund.fund_name_th }}` is not a string until something binds it to a fund. So with no fund
 *    picked the column keeps doing what it always did — parse the box, draw the structure — and the
 *    moment a fund is picked the same document goes to `PreviewCardConfig`, which renders it against
 *    that fund's real rows in the chosen language and sends the finished envelope back as TEXT.
 *    Three rules ride along, all of them borrowed from `TaxCardPreviewPanelV2` and all of them the
 *    kind that fail invisibly:
 *      · A LATE ANSWER IS NOT AN ANSWER. Requests are debounced and still overlap; every one carries a
 *        `previewSeq` ticket and a reply that is not holding the newest ticket is dropped in silence.
 *      · AN ERROR NEVER CLEARS THE LAST GOOD CARD. Typing JSON means being unparseable several times a
 *        line. The warning goes ABOVE the card the last successful render produced; that card stays.
 *      · A CARD EXISTS WHEN `raw` DOES, NOT WHEN `errors` IS EMPTY. The endpoint ships soft errors
 *        (a language gap, a `refId` it filled in) alongside a perfectly good card.
 */
import draggable from 'vuedraggable';
import MutualFundCardPreview from './MutualFundCardPreview.vue';
import { canFormatJsonText, formatJsonText } from '../utils/jsonText';
import {
  CODE_PATTERN,
  MAX_NAME_CHARS,
  applySortOrder,
  blankCardConfigItem,
  detectCardType,
  parseCardConfig,
  validateCardConfigItems,
} from '../utils/mutualFundCardConfig';
import { CARD_CONFIG_SAMPLES } from '../utils/mutualFundCardSamples';

/** Beyond this many distinct documents the type memo is dropped rather than grown without limit. */
const CARD_TYPE_MEMO_LIMIT = 200;

/** How long the admin has to stop typing in the fund box before it asks the server for matches. */
const FUND_SEARCH_DEBOUNCE_MS = 350;

/** How many funds one search asks for. The server clamps this to 1..50 anyway. */
const FUND_SEARCH_LIMIT = 20;

/**
 * How long the admin has to stop changing things before the card is re-rendered against live rows.
 * Long enough that a word costs one round-trip instead of eight, short enough to still read as live —
 * the same 400 ms `TaxCardPreviewPanelV2` settled on, and there is no refresh button to fall back on.
 */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * 🔴 The ceiling the SERVER enforces on a preview (`MutualFundController.PreviewCardConfig` caps
 * `fundCodes` at 10). Picking an eleventh does not fail — it is silently ignored on the other side —
 * so the extra picks are cut here, where there is somewhere to say so.
 */
const MAX_PREVIEW_FUNDS = 10;

/** Which languages the toggle offers, and the only values `lang` may take on the wire. */
const PREVIEW_LANGS = ['th', 'en', 'zh'];

/**
 * How many searched-for funds are remembered for their names. A chip whose fund has scrolled out of
 * the current result set would otherwise fall back to printing the bare code.
 */
const KNOWN_FUND_LIMIT = 200;

export default {
  name: 'MutualFundCardConfigDialog',
  components: { draggable, MutualFundCardPreview },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      items: [],
      selectedRowKey: null,
      rowKeySeed: 0,
      loading: false,
      saving: false,
      loadError: '',
      saveError: '',
      saveSuccess: '',
      /** Notes the server attached to the last SUCCESSFUL save. Optional field — older builds of the
       *  endpoint do not send it, and an absent one must read as "no notes", never as a crash. */
      saveWarnings: [],
      pendingSample: null,

      // ── the fund picker ───────────────────────────────────────────────────────────────────────
      /** Fund codes the preview binds to. Empty = draw the document as typed, ask the server nothing. */
      previewFundCodes: [],
      /** Language the preview asks for. One of `PREVIEW_LANGS`. */
      previewLang: 'th',
      /** The picker's search box, controlled here because the request is debounced off it. */
      fundSearch: '',
      /** Normalised rows from the last search — `{ fundCode, mStarId, name, title }`. */
      fundResults: [],
      /** Every fund seen so far, so a picked chip keeps its NAME after the results move on. */
      knownFunds: [],
      fundSearchLoading: false,
      fundSearchTimer: null,
      /** Ticket of the newest search in flight; a reply not holding it is dropped. */
      fundSearchSeq: 0,
      /** Has the list been filled once? Until then, focusing the box fetches the first page. */
      fundLoadedOnce: false,
      /** One-off line under the picker — currently only "you picked more than the server takes". */
      fundNotice: '',

      // ── the server-rendered preview ───────────────────────────────────────────────────────────
      previewLoading: false,
      /** Envelope of the last render that WORKED. Never overwritten by one that did not. */
      serverCard: null,
      /** That render's JSON, verbatim from the server — never re-stringified from `serverCard`. */
      serverRaw: '',
      /** What the server said about the CURRENT attempt (warnings included — see rule 3). */
      previewErrors: [],
      /** Language the server actually rendered in; differs from `previewLang` when a bag had a gap. */
      previewUsedLang: '',
      /** Did the current attempt fail to produce a card? `serverCard` may still hold an older one. */
      previewFailed: false,
      previewTimer: null,
      /** Ticket of the newest preview in flight. */
      previewSeq: 0,
      /** Signature already on screen — re-asking for it would spend a round-trip to redraw the same. */
      renderedSignature: null,
    };
  },
  created() {
    // Deliberately NOT in data(): a Map of parsed card types is a cache, not state, and making it
    // reactive would have every row's chip re-render whenever any row is typed in.
    this.cardTypeMemo = new Map();
  },
  watch: {
    modelValue(open) {
      if (!open) {
        // Closing does not unmount the dialog, so nothing here stops on its own: drop the timers and
        // burn the tickets, or a reply lands in a dialog that is no longer on screen.
        this.cancelPendingWork();
        return;
      }
      this.resetState();
      this.loadConfigs();
    },
    /**
     * A different row is a different document. Whatever the server rendered for the previous one has
     * to go with it — leaving it up would show one card while the box beside it holds another.
     */
    selectedRowKey() {
      this.resetPreviewRender();
    },
    /**
     * The one trigger for a live render, and it covers all four things that can change what the card
     * should look like: the document, the row, the funds, the language. Empty = no fund picked, i.e.
     * back to the in-memory parse, and nothing to ask for.
     */
    previewSignature(value) {
      if (!value) {
        this.cancelPreviewTimer();
        this.previewLoading = false;
        return;
      }
      this.schedulePreview(PREVIEW_DEBOUNCE_MS);
    },
  },
  mounted() {
    if (this.modelValue) {
      this.resetState();
      this.loadConfigs();
    }
  },
  computed: {
    dialogModel: {
      get() { return this.modelValue; },
      set(value) { this.$emit('update:modelValue', value); },
    },
    /** `<draggable>`'s v-model. The setter renumbers, because a drag moves rows and no number with them. */
    draggableItems: {
      get() { return this.items; },
      set(value) {
        this.items = applySortOrder(value);
        this.saveSuccess = '';
      },
    },
    selectedItem() {
      return this.items.find((item) => item.rowKey === this.selectedRowKey) || null;
    },
    /** Parsed ONCE per change and read by both the error line and the preview — never parsed twice. */
    selectedParse() {
      return this.selectedItem
        ? parseCardConfig(this.selectedItem.configJson)
        : { card: null, error: null };
    },
    parsedCard() {
      return this.selectedParse.card;
    },
    selectedJsonError() {
      return this.selectedParse.error;
    },
    validationErrors() {
      return validateCardConfigItems(this.items);
    },
    canSave() {
      return !this.validationErrors.length && !this.saving && !this.loading;
    },
    dragDisabled() {
      return this.loading || this.saving || this.items.length < 2;
    },
    codeRules() {
      return [
        (value) => !!String(value == null ? '' : value).trim() || 'A config code is required.',
        (value) => CODE_PATTERN.test(String(value == null ? '' : value).trim().toUpperCase())
          || 'Use A-Z, 0-9 and underscore only, up to 40 characters.',
      ];
    },
    samples() {
      return CARD_CONFIG_SAMPLES;
    },
    maxNameChars() {
      return MAX_NAME_CHARS;
    },
    sampleConfirmOpen: {
      get() { return !!this.pendingSample; },
      set(value) { if (!value) this.pendingSample = null; },
    },

    // ── the preview's two modes ───────────────────────────────────────────────────────────────────
    /**
     * What the picker shows: the current search results, plus every picked fund that is not among them.
     *
     * The second half is the load-bearing one. Vuetify draws a chip from the ITEM whose value matches,
     * and prints the bare value when there is none — so a fund picked from one search would lose its
     * name as soon as the next search replaced the list.
     */
    fundPickerItems() {
      const seen = Object.create(null);
      const rows = [];
      this.fundResults.forEach((row) => {
        if (!row || !row.fundCode || seen[row.fundCode]) return;
        seen[row.fundCode] = true;
        rows.push(row);
      });
      this.previewFundCodes.forEach((code) => {
        if (!code || seen[code]) return;
        seen[code] = true;
        const known = this.knownFunds.find((entry) => entry.fundCode === code);
        rows.push(known || { fundCode: code, mStarId: '', name: '', title: code });
      });
      return rows;
    },
    fundPickerHint() {
      if (this.fundNotice) return this.fundNotice;
      if (!this.previewFundCodes.length) return 'No fund picked — the preview draws the document as typed.';
      return `Rendered from live fund data · up to ${MAX_PREVIEW_FUNDS} funds.`;
    },
    /**
     * "Has anything that changes the rendered card changed?" — byte-identical while unrelated fields
     * (name, code, another row's JSON) are edited, and EMPTY whenever there is nothing to render:
     * no row selected, or no fund to bind the template's paths to.
     */
    previewSignature() {
      if (!this.selectedItem || !this.previewFundCodes.length) return '';
      return JSON.stringify({
        row: this.selectedRowKey,
        lang: this.previewLang,
        funds: this.previewFundCodes,
        json: String(this.selectedItem.configJson || ''),
      });
    },
    /**
     * The pair handed to `MutualFundCardPreview` — always a card and the TEXT it was parsed from, from
     * the same source, because the component tells "nothing typed" from "does not parse" by looking at
     * `raw`.
     *
     * 🔴 `serverRaw` only ever holds a render that WORKED, so a failed attempt leaves the last good
     * card standing (rule 2). And until the first live render lands, the structural parse stays up
     * rather than flashing the column empty for a round-trip.
     */
    previewSource() {
      const local = {
        card: this.parsedCard,
        raw: this.selectedItem ? String(this.selectedItem.configJson || '') : '',
      };
      if (!this.previewFundCodes.length) return local;
      return this.serverRaw ? { card: this.serverCard, raw: this.serverRaw } : local;
    },
    /**
     * Set only when the server answered in a language other than the one asked for. That is
     * `TaxCardLangV2` reporting a gap: one label somewhere had no text in that language, so the WHOLE
     * document was collapsed again in Thai rather than shipped half-translated.
     */
    previewLangNote() {
      const used = String(this.previewUsedLang || '').trim().toLowerCase();
      const asked = String(this.previewLang || '').trim().toLowerCase();
      if (!used || !asked || !this.serverRaw || used === asked) return '';
      return `Rendered in ${used.toUpperCase()} — some labels have no ${asked.toUpperCase()} text.`;
    },
  },
  methods: {
    canFormatJsonText,

    // ── loading and saving ────────────────────────────────────────────────────────────────────────
    resetState() {
      this.items = [];
      this.selectedRowKey = null;
      this.rowKeySeed = 0;
      this.loadError = '';
      this.saveError = '';
      this.saveSuccess = '';
      this.saveWarnings = [];
      this.pendingSample = null;
      this.cardTypeMemo.clear();
      // The picker and the rendered card belong to the session that is ending, not to the next one.
      this.previewLang = 'th';
      this.resetFundPicker();
      this.resetPreviewRender();
    },
    loadConfigs() {
      this.loading = true;
      this.loadError = '';
      this.$call.GetMutualFundCardConfigs(
        (response) => {
          this.loading = false;
          if (response && response.status === 0) {
            this.applyData(response);
            return;
          }
          this.loadError = (response && response.message) || 'The card configs could not be loaded.';
        },
        (failure) => {
          this.loading = false;
          this.loadError = this.readServerMessage(failure) || 'The card configs could not be loaded.';
        },
      );
    },
    /**
     * Take the server's list as the new truth.
     *
     * Row keys are minted fresh every time (the server does not send one), so the SELECTION has to be
     * re-established by `configCode` — the only identity that survives a round trip. Without this, saving
     * would drop the admin back to "nothing selected" and lose their place in a list of twenty.
     */
    applyData(response) {
      const keepCode = this.normalizedCode(this.selectedItem ? this.selectedItem.configCode : '');
      const rows = Array.isArray(response && response.data && response.data.items)
        ? response.data.items
        : [];

      this.items = applySortOrder(rows.map((row) => ({
        rowKey: this.nextRowKey(),
        id: Number(row && row.id) || 0,
        configCode: String((row && row.configCode) || ''),
        configName: String((row && row.configName) || ''),
        configJson: String((row && row.configJson) || ''),
        sortOrder: Number(row && row.sortOrder) || 0,
        isActive: !(row && row.isActive === false),
      })));

      const restored = keepCode
        ? this.items.find((item) => this.normalizedCode(item.configCode) === keepCode)
        : null;
      this.selectedRowKey = (restored || this.items[0] || {}).rowKey || null;
    },
    /**
     * What goes on the wire: no `id` and no `cardType`. The server matches rows by `configCode`, derives
     * the card type from the JSON's own `type`, and renumbers `sortOrder` from the order these arrive in.
     */
    buildPayload() {
      return {
        items: this.items.map((item, index) => ({
          configCode: this.normalizedCode(item.configCode),
          configName: String(item.configName || '').trim(),
          configJson: String(item.configJson || '').trim(),
          sortOrder: index + 1,
          isActive: Boolean(item.isActive),
        })),
      };
    },
    saveConfigs() {
      if (!this.canSave) return;

      this.saving = true;
      this.saveError = '';
      this.saveSuccess = '';
      this.saveWarnings = [];
      this.$call.SaveMutualFundCardConfigs(
        this.buildPayload(),
        (response) => {
          this.saving = false;
          if (response && response.status === 0) {
            this.applyData(response);
            this.saveSuccess = 'Saved successfully.';
            this.saveWarnings = this.readSaveWarnings(response);
            return;
          }
          this.saveError = (response && response.message) || 'The card configs could not be saved.';
        },
        (failure) => {
          this.saving = false;
          this.saveError = this.readServerMessage(failure) || 'The card configs could not be saved.';
        },
      );
    },
    /**
     * The optional per-row notes a SUCCESSFUL save can carry (a template path the validator could not
     * resolve, say — a warning, not a rejection: the row was written).
     *
     * 🔴 Deliberately shrugs at every shape but the one it wants. The field is newer than this dialog,
     * so a server that does not send it, sends `null`, or sends something that is not an array must
     * read as "no notes" — never as a broken save screen on top of a save that worked.
     */
    readSaveWarnings(response) {
      const data = response && response.data;
      const list = (data && Array.isArray(data.warnings) && data.warnings)
        || (response && Array.isArray(response.warnings) && response.warnings)
        || [];
      return list.map((message) => String(message == null ? '' : message).trim()).filter((m) => m);
    },
    /** The BAL's `ArgumentException` text, which arrives as the raw body of a 400. */
    readServerMessage(failure) {
      const body = failure && failure.response ? failure.response.data : null;
      if (typeof body === 'string' && body.trim()) return body.trim();
      if (body && typeof body.message === 'string' && body.message.trim()) return body.message.trim();
      return '';
    },
    closeDialog() {
      this.dialogModel = false;
    },

    // ── the list ──────────────────────────────────────────────────────────────────────────────────
    nextRowKey() {
      this.rowKeySeed += 1;
      return `card-config-${this.rowKeySeed}`;
    },
    normalizedCode(value) {
      return String(value == null ? '' : value).trim().toUpperCase();
    },
    selectRow(item) {
      if (!item) return;
      this.selectedRowKey = item.rowKey;
    },
    addItem() {
      const row = blankCardConfigItem(this.nextRowKey());
      this.items.push(row);
      applySortOrder(this.items);
      this.selectedRowKey = row.rowKey;
      this.saveSuccess = '';
    },
    removeItem(index) {
      const [removed] = this.items.splice(index, 1);
      if (!removed) return;
      applySortOrder(this.items);
      if (this.selectedRowKey === removed.rowKey) {
        // Keep an editor on screen: the row that slid into this position, else the last one.
        const next = this.items[index] || this.items[this.items.length - 1] || null;
        this.selectedRowKey = next ? next.rowKey : null;
      }
      this.saveSuccess = '';
    },
    moveItem(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.items.length) return;
      const rows = [...this.items];
      const [moved] = rows.splice(index, 1);
      rows.splice(target, 0, moved);
      this.items = applySortOrder(rows);
      this.saveSuccess = '';
    },
    setActive(item, value) {
      item.isActive = value === true;
      this.saveSuccess = '';
    },
    /**
     * The card type shown on a row's chip, memoised BY DOCUMENT TEXT.
     *
     * Called from the template for every row on every render, and typing in the JSON box re-renders the
     * whole list — without the memo a twenty-row library would re-parse twenty documents per keystroke.
     */
    cardTypeOf(json) {
      const key = typeof json === 'string' ? json : '';
      if (this.cardTypeMemo.has(key)) return this.cardTypeMemo.get(key);
      const type = detectCardType(parseCardConfig(key).card);
      if (this.cardTypeMemo.size >= CARD_TYPE_MEMO_LIMIT) this.cardTypeMemo.clear();
      this.cardTypeMemo.set(key, type);
      return type;
    },

    // ── the selected card's fields ────────────────────────────────────────────────────────────────
    setName(value) {
      if (!this.selectedItem) return;
      this.selectedItem.configName = String(value == null ? '' : value);
      this.saveSuccess = '';
    },
    setCode(value) {
      if (!this.selectedItem) return;
      // Upper-cased on the way in, and inner whitespace dropped: a space is not a legal code character
      // and letting one sit in the box only to be rejected at SAVE is a slower way of saying the same.
      this.selectedItem.configCode = String(value == null ? '' : value).toUpperCase().replace(/\s+/g, '');
      this.saveSuccess = '';
    },
    setJson(value) {
      if (!this.selectedItem) return;
      this.selectedItem.configJson = String(value == null ? '' : value);
      this.saveSuccess = '';
    },
    beautifyJson() {
      if (!this.selectedItem) return;
      const formatted = formatJsonText(this.selectedItem.configJson);
      // `null` means the document does not parse. Leaving it alone is the contract: this button must
      // never be the thing that damages a half-typed card.
      if (formatted === null) return;
      this.selectedItem.configJson = formatted;
      this.saveSuccess = '';
    },
    requestSample(sample) {
      if (!this.selectedItem || !sample) return;
      if (String(this.selectedItem.configJson || '').trim() === '') {
        this.applySample(sample);
        return;
      }
      this.pendingSample = sample;
    },
    confirmSample() {
      const sample = this.pendingSample;
      this.pendingSample = null;
      this.applySample(sample);
    },
    applySample(sample) {
      if (!this.selectedItem || !sample) return;
      this.selectedItem.configJson = sample.json;
      this.saveSuccess = '';
    },

    // ── the fund picker ───────────────────────────────────────────────────────────────────────────
    /** First focus fills the empty list, so the box is never a blank menu waiting to be typed into. */
    onPickerFocus(isFocused) {
      if (isFocused) this.ensureFundOptions();
    },
    ensureFundOptions() {
      if (this.fundLoadedOnce || this.fundSearchLoading || this.fundSearchTimer !== null) return;
      this.scheduleFundSearch(0);
    },
    /**
     * The search box is CONTROLLED from here (the request is debounced off it), so this handler is the
     * only thing that may move it. Vuetify also fires this with '' right after a pick — that reload of
     * the default page is wanted, so it is not filtered out.
     */
    onFundSearch(value) {
      const text = String(value == null ? '' : value);
      if (text === this.fundSearch) return;
      this.fundSearch = text;
      this.scheduleFundSearch(FUND_SEARCH_DEBOUNCE_MS);
    },
    scheduleFundSearch(delay) {
      this.cancelFundSearchTimer();
      this.fundSearchTimer = window.setTimeout(() => {
        this.fundSearchTimer = null;
        this.runFundSearch();
      }, delay);
    },
    cancelFundSearchTimer() {
      if (this.fundSearchTimer === null) return;
      window.clearTimeout(this.fundSearchTimer);
      this.fundSearchTimer = null;
    },
    /**
     * 🔴 No inline error on a failed search, and no Swal either: `api/call.js` already raises one, and
     * a keyword that found nothing is not a broken dialog. The list is simply left as it was.
     */
    runFundSearch() {
      const seq = this.fundSearchSeq + 1;
      this.fundSearchSeq = seq;
      this.fundSearchLoading = true;
      this.$call.SearchMutualFundFunds(
        { q: this.fundSearch, limit: FUND_SEARCH_LIMIT },
        (response) => {
          if (seq !== this.fundSearchSeq) return; // a newer search owns the list now
          this.fundSearchLoading = false;
          this.fundLoadedOnce = true;
          if (response && response.status === 0) this.applyFundResults(response.data);
        },
        () => {
          if (seq !== this.fundSearchSeq) return;
          this.fundSearchLoading = false;
        },
      );
    },
    applyFundResults(data) {
      const rows = [];
      (Array.isArray(data) ? data : []).forEach((row) => {
        const fundCode = String((row && row.fundCode) || '').trim();
        if (!fundCode) return;
        const name = String((row && row.name) || '').trim();
        rows.push({
          fundCode,
          mStarId: String((row && row.mStarId) || '').trim(),
          name,
          // What the chip and the row read. A fund with no name in the view still has to be pickable.
          title: name || fundCode,
        });
      });
      this.fundResults = rows;
      this.rememberFunds(rows);
    },
    /**
     * Remember the names. When the memory is full it keeps the PICKED funds and the newest results and
     * drops the rest — trimming blindly from the front could take the name off a chip on screen.
     */
    rememberFunds(rows) {
      const known = this.knownFunds.slice();
      rows.forEach((row) => {
        if (known.some((entry) => entry.fundCode === row.fundCode)) return;
        known.push(row);
      });
      if (known.length <= KNOWN_FUND_LIMIT) {
        this.knownFunds = known;
        return;
      }
      const kept = known.filter((entry) => this.previewFundCodes.indexOf(entry.fundCode) !== -1);
      const keptCodes = kept.map((entry) => entry.fundCode);
      this.knownFunds = kept.concat(rows.filter((row) => keptCodes.indexOf(row.fundCode) === -1));
    },
    /**
     * Take the picks, deduplicated and trimmed to what the server will actually read.
     *
     * 🔴 The eleventh fund is dropped HERE rather than on the wire, because the server drops it in
     * silence: a card that quietly compared ten of the twelve funds an admin chose would look like a
     * template bug for as long as it took to notice.
     */
    setPreviewFunds(value) {
      const codes = [];
      (Array.isArray(value) ? value : []).forEach((entry) => {
        const code = String(entry == null ? '' : entry).trim();
        if (code && codes.indexOf(code) === -1) codes.push(code);
      });
      if (codes.length > MAX_PREVIEW_FUNDS) {
        codes.length = MAX_PREVIEW_FUNDS;
        this.fundNotice = `The preview takes at most ${MAX_PREVIEW_FUNDS} funds — the rest were dropped.`;
      } else {
        this.fundNotice = '';
      }
      this.previewFundCodes = codes;
    },
    /** `mandatory` on the toggle means a click on the active button emits nothing usable — keep it. */
    setPreviewLang(value) {
      const lang = String(value == null ? '' : value).trim().toLowerCase();
      if (PREVIEW_LANGS.indexOf(lang) === -1) return;
      this.previewLang = lang;
    },
    resetFundPicker() {
      this.cancelFundSearchTimer();
      // Anything still in flight answers to a ticket that can no longer be the newest.
      this.fundSearchSeq += 1;
      this.fundSearchLoading = false;
      this.previewFundCodes = [];
      this.fundSearch = '';
      this.fundResults = [];
      this.knownFunds = [];
      this.fundLoadedOnce = false;
      this.fundNotice = '';
    },

    // ── the server-rendered preview ───────────────────────────────────────────────────────────────
    cancelPreviewTimer() {
      if (this.previewTimer === null) return;
      window.clearTimeout(this.previewTimer);
      this.previewTimer = null;
    },
    /** Queue a render `delay` ms from now, replacing whatever was queued before. */
    schedulePreview(delay) {
      this.cancelPreviewTimer();
      if (!this.previewSignature) return;
      this.previewTimer = window.setTimeout(() => {
        this.previewTimer = null;
        this.fetchPreview();
      }, delay);
    },
    /**
     * Ask the server to render THIS document against the picked funds.
     *
     * The reply is always 200, so the callback's job is to read it, not to test it — except for the
     * one thing that decides whether a card came back at all, which is `raw` and never `errors`
     * (`applyPreviewResult`).
     */
    fetchPreview() {
      const item = this.selectedItem;
      const signature = this.previewSignature;
      // Re-checked here and not only in `schedule`: the row can change, or the picks can be cleared,
      // during the debounce window.
      if (!item || !signature) return;
      // Already on screen and it worked — nothing to ask for. A signature that FAILED is asked again,
      // because re-picking is the only retry an admin has for a call that died on the transport.
      if (signature === this.renderedSignature && !this.previewFailed) return;

      const seq = this.previewSeq + 1;
      this.previewSeq = seq;
      this.previewLoading = true;
      this.$call.PreviewMutualFundCardConfig(
        {
          configJson: String(item.configJson || ''),
          fundCodes: this.previewFundCodes.slice(),
          lookupType: 'fundCode',
          lang: this.previewLang,
        },
        (response) => {
          if (seq !== this.previewSeq) return; // a newer request owns the column now
          this.previewLoading = false;
          this.renderedSignature = signature;
          if (response && response.status === 0) {
            this.applyPreviewResult(response.data);
            return;
          }
          // An envelope that says "not ok" is not a broken document — say so in its own words and,
          // per rule 2, leave the card standing.
          this.previewFailed = true;
          this.previewErrors = [(response && response.message) || 'The preview could not be rendered.'];
        },
        (failure) => {
          if (seq !== this.previewSeq) return;
          this.previewLoading = false;
          this.renderedSignature = signature;
          this.previewFailed = true;
          this.previewErrors = [
            this.readServerMessage(failure) || 'The preview could not be rendered.',
          ];
        },
      );
    },
    /**
     * Fold one server result into the column.
     *
     * 🔴 Rule 3 lives here: A CARD EXISTS WHEN `raw` DOES. `errors` can be non-empty on a perfectly
     * good card — a language gap and a filled-in `refId` are both reported there — so testing it
     * instead would throw away cards the server meant to ship.
     *
     * 🔴 Rule 2 lives here too: `serverCard`/`serverRaw` are only ever overwritten by a render that
     * produced a card. One that produced none updates the messages and nothing else.
     *
     * 🔴 `raw` is stored as the SERVER'S string, verbatim, never re-stringified from the parsed object.
     */
    applyPreviewResult(data) {
      const result = data && typeof data === 'object' ? data : {};
      const errors = Array.isArray(result.errors) ? result.errors : [];
      this.previewErrors = errors
        .map((message) => String(message == null ? '' : message).trim())
        .filter((message) => message);

      const raw = typeof result.raw === 'string' ? result.raw : '';
      const parsed = parseCardConfig(raw);
      if (parsed.card) {
        this.serverCard = parsed.card;
        this.serverRaw = raw;
        this.previewUsedLang = String(result.usedLang || '').trim();
        this.previewFailed = false;
        return;
      }
      // No `raw` at all ⇒ the server already said why in `errors`; a `raw` this side cannot read is a
      // second thing worth saying. Either way the last good card stays on screen.
      if (parsed.error) this.previewErrors = this.previewErrors.concat([parsed.error]);
      this.previewFailed = true;
    },
    resetPreviewRender() {
      this.cancelPreviewTimer();
      this.previewSeq += 1;
      this.previewLoading = false;
      this.serverCard = null;
      this.serverRaw = '';
      this.previewErrors = [];
      this.previewUsedLang = '';
      this.previewFailed = false;
      this.renderedSignature = null;
    },
    /** Everything that can still fire after the dialog stops being on screen. */
    cancelPendingWork() {
      this.cancelFundSearchTimer();
      this.cancelPreviewTimer();
      this.fundSearchSeq += 1;
      this.previewSeq += 1;
      this.fundSearchLoading = false;
      this.previewLoading = false;
    },
  },
  beforeUnmount() {
    this.cancelPendingWork();
  },
};
</script>

<style scoped>
.mfcc-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.mfcc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 12px;
}

.mfcc-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.mfcc-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
}

.mfcc-title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
}

.mfcc-subtitle {
  font-size: 0.78rem;
  font-weight: 400;
  line-height: 1.35;
  white-space: normal;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* 🔴 The load-bearing rule. `v-dialog scrollable` makes `v-card-text` the ONE scroller; while it keeps
   that job both columns move together and the preview slides off the moment the list is scrolled.
   Hidden here = each column owns its own scroller, below. */
.mfcc-split {
  display: flex;
  overflow: hidden;
  padding: 0;
}

.mfcc-list-col {
  flex: 0 0 360px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
  border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.mfcc-list-head {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px 10px;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.mfcc-list-title {
  font-size: 0.9rem;
  font-weight: 600;
}

.mfcc-list-count {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.mfcc-list-empty {
  padding: 24px 16px;
  font-size: 0.82rem;
  line-height: 1.5;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.mfcc-rows {
  padding: 8px;
}

.mfcc-list-foot {
  padding: 0 8px 12px;
}

.mfcc-row {
  padding: 8px 8px 6px;
  margin-bottom: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
}

.mfcc-row:hover {
  border-color: rgba(var(--v-theme-primary), 0.4);
}

.mfcc-row--selected {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: inset 3px 0 0 0 rgb(var(--v-theme-primary));
}

/* Switched off, not deleted — dimmed so the difference is visible without reading the switch. */
.mfcc-row--off {
  opacity: 0.62;
}

.mfcc-row-line {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.mfcc-row-line--meta {
  justify-content: space-between;
  margin-top: 2px;
}

.mfcc-row-order {
  flex: 0 0 auto;
}

.mfcc-row-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  font-size: 0.86rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mfcc-row-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding-left: 4px;
}

.mfcc-row-code {
  overflow: hidden;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), 0.68);
}

.mfcc-row-type {
  flex: 0 0 auto;
  max-width: 130px;
}

.mfcc-row-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0;
}

.mfcc-row-switch {
  flex: 0 0 auto;
  margin-right: 2px;
  transform: scale(0.78);
  transform-origin: center;
}

.mfcc-drag-ghost {
  opacity: 0.45;
}

.mfcc-drag-chosen {
  border-color: rgb(var(--v-theme-primary));
}

.mfcc-editor-col {
  flex: 1 1 auto;
  /* Without this a long unbroken value sets the flex item's min-content width and the editor pushes the
     list column off the dialog instead of wrapping. */
  min-width: 0;
  overflow-y: auto;
  padding: 16px 20px 24px;
}

.mfcc-editor-split {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.mfcc-form-col {
  flex: 1 1 auto;
  min-width: 0;
}

.mfcc-preview-col {
  flex: 0 0 400px;
  padding: 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.mfcc-preview-label {
  margin-bottom: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* The bar that binds the card to real data. It sits ABOVE the card and stays a fixed height whatever
   it is saying, so picking a fund never shifts the card underneath it. */
.mfcc-preview-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.mfcc-preview-lang-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.mfcc-lang-toggle {
  flex: 0 0 auto;
  height: 28px;
}

/* The gap note can be a sentence; it wraps instead of pushing the toggle out of the column. */
.mfcc-lang-note {
  min-width: 0;
  height: auto;
  padding-top: 3px;
  padding-bottom: 3px;
  white-space: normal;
  line-height: 1.3;
}

/* `:active` keeps the bar in the layout at all times — a bar that appeared and disappeared would move
   the card down and up again on every keystroke. */
.mfcc-preview-bar {
  margin-bottom: 6px;
}

.mfcc-preview-alert {
  margin-bottom: 8px;
}

.mfcc-json-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 18px 0 6px;
}

.mfcc-json-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.82);
}

/* A card document is read structurally — indentation and alignment carry the meaning — so it gets a
   monospaced face and a tighter line height than the rest of the form. */
.code-area :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.8rem;
  line-height: 1.45;
}

.mfcc-error-list {
  padding-left: 18px;
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.5;
}

.mfcc-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 60px 24px;
  text-align: center;
}

.mfcc-placeholder-title {
  font-size: 0.95rem;
  font-weight: 600;
}

.mfcc-placeholder-text {
  max-width: 380px;
  font-size: 0.82rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* Under ~1280px the form and the preview cannot both stay usable side by side, so the preview drops
   below the editor and the right-hand column keeps its single scroller. */
@media (max-width: 1280px) {
  .mfcc-editor-split {
    flex-direction: column;
  }

  .mfcc-preview-col {
    flex: 0 0 auto;
    width: 100%;
  }
}

/* Narrower still and two columns leave neither usable: they stack and the dialog goes back to being ONE
   scroller — the columns must give up their own, or the lower one becomes a scroll trap. */
@media (max-width: 900px) {
  .mfcc-split {
    flex-direction: column;
    overflow-y: auto;
  }

  .mfcc-list-col {
    flex: 0 0 auto;
    overflow: visible;
    border-right: none;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  }

  .mfcc-editor-col {
    overflow: visible;
  }
}
</style>
